import { useEffect, useState } from 'react'

/**
 * Altura visível real no mobile — `100dvh`/`100vh` podem exceder a área útil no
 * iOS Chrome/Safari quando a barra de endereço está aberta. `visualViewport`
 * reflete o retângulo que o usuário de fato vê.
 */
function useVisibleViewportHeight() {
  const [height, setHeight] = useState(null)

  useEffect(() => {
    const read = () => {
      if (typeof window === 'undefined') return
      const vv = window.visualViewport
      setHeight(Math.round(vv?.height ?? window.innerHeight))
    }

    read()
    const vv = window.visualViewport
    vv?.addEventListener('resize', read)
    vv?.addEventListener('scroll', read)
    window.addEventListener('resize', read)

    return () => {
      vv?.removeEventListener('resize', read)
      vv?.removeEventListener('scroll', read)
      window.removeEventListener('resize', read)
    }
  }, [])

  return height
}

/**
 * Raiz da tela de execução de nível.
 *
 * Ocupa a viewport inteira e estabelece a coluna flex que ancora a toolbar no
 * topo e o footer na base. Toda a ancoragem é composição de flex — `flex-1`
 * combinado com `min-h-0` em cada nível — e não medição de altura em JS, exceto
 * o sync de `visualViewport` acima para corrigir iOS.
 *
 * Altura em duas camadas:
 * 1. CSS — `h-full` na cadeia `AppShell → PassthroughShell → Screen`, com teto
 *    `max-h-[min(100svh,100dvh)]` para nunca ultrapassar a menor viewport.
 * 2. JS — quando `visualViewport` existe, fixa altura em px da área visível
 *    (barra de endereço incluída/excluída), evitando footer fora da tela.
 */
// `style` com default explícito: sem ele o TypeScript infere a prop como
// obrigatória a partir do JSX, e todo consumidor .tsx é forçado a passar
// `style={undefined}`. Em runtime é idêntico — o valor já era `undefined`.
export const Screen = ({ children, className = '', style = undefined, ...rest }) => {
  const viewportHeight = useVisibleViewportHeight()

  return (
    <div
      id="level-play-wrapper"
      className={`flex h-full min-h-0 w-full max-h-[min(100svh,100dvh)] flex-col overflow-hidden bg-white ${className}`}
      style={
        viewportHeight != null
          ? { ...style, height: viewportHeight, maxHeight: viewportHeight }
          : style
      }
      {...rest}
    >
      {children}
    </div>
  )
}
